import { Loader } from '@googlemaps/js-api-loader';
import React, { useEffect, useRef, useState } from 'react';
import ReactSelect from 'react-select';
import { PartnerState } from '../../tools/apiTools';
import { RequirementsProps } from '../../tools/context';
import { useIsClient } from '../../tools/hooks';
import { useTranslation } from '../../tools/i18n';
import { Picto } from '../Picto';
import { FilterLayout } from './FiltersLayout';
import { TravelPlannerSearchMapType } from './types';

interface PlaceOfDeparture {
  step: TravelPlannerSearchMapType;
  siteConfiguration: any;
  partnerStates: PartnerState[];
  agentId: string;
  setStateData: (stateData: any) => void;
  stateData: any;
  callback: (parnerState: PartnerState) => void;
  config: any;
  requirements: RequirementsProps;
  back: () => void;
}

interface Coordinates {
  name: string;
  lat: number;
  lon: number;
}

export const PlaceOfDeparture = (props: PlaceOfDeparture) => {
  const { config, stateData, requirements, callback, back } = props;
  const [isPlaceOfArrival, setIsPlaceOfArrival] = useState(
    Boolean(stateData?.placeOfArrival) || false
  );
  const [predictions, setPredictions] = useState<any[]>([]);
  const [arrivalPredictions, setArrivalPredictions] = useState<any[]>([]);
  const autocomplete = useRef<google.maps.places.AutocompleteService>();
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken>();
  const voidRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const isClient = useIsClient();

  useEffect(() => {
    if (isClient && !window.google) {
      const loader = new Loader({
        apiKey: 'AIzaSyCYp4mEcSL6CN2IjfVnVjnJDdWCWeGhUz8',
        version: 'weekly',
        libraries: ['places'],
      });

      loader.importLibrary('places').catch((error) => {
        console.error('Erreur lors du chargement de Google Maps:', error);
      });
    }
  }, [isClient]);

  const getPredictions = async (input: string) => {
    try {
      if (!autocomplete.current) return { results: [], input };

      if (!sessionToken.current) {
        sessionToken.current = new google.maps.places.AutocompleteSessionToken();
      }

      const r = await autocomplete.current.getPlacePredictions({
        input,
        types: ['locality', 'postal_code', 'colloquial_area'],
        sessionToken: sessionToken.current,
        componentRestrictions: {
          country: ['fr', 'pt', 'be', 'es'],
        },
      });

      return {
        results: r?.predictions || [],
        input,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des prédictions:', error);
      return { results: [], input };
    }
  };

  const handleInput = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;

    if (!value) return setPredictions([]);

    if (!autocomplete.current) {
      if (isClient && window.google) {
        autocomplete.current = new google.maps.places.AutocompleteService();
      } else {
        return;
      }
    }

    getPredictions(value).then((r) => {
      setPredictions(r.results);
    });
  };

  const handleArrivalInput = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;

    if (!value) return setArrivalPredictions([]);

    if (!autocomplete.current) {
      if (isClient && window.google) {
        autocomplete.current = new google.maps.places.AutocompleteService();
      } else {
        return;
      }
    }

    getPredictions(value).then((r) => {
      setArrivalPredictions(r.results);
    });
  };

  const getCoordinates = async (placeId: string): Promise<Coordinates | null> => {
    try {
      if (!voidRef.current) {
        console.warn('voidRef non initialisé');
        return null;
      }

      return new Promise((resolve, reject) => {
        const service = new google.maps.places.PlacesService(voidRef.current);
        service.getDetails(
          {
            placeId,
            fields: ['address_components', 'geometry', 'name'],
            sessionToken: sessionToken.current,
          },
          (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && result) {
              resolve({
                name: result.name || '',
                lat: result.geometry?.location.lat() || 0,
                lon: result.geometry?.location.lng() || 0,
              });
            } else {
              reject(`Failed to fetch coordinates: ${status}`);
            }
          }
        );
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des coordonnées:', error);
      return null;
    }
  };

  const handleSelectPrediction = async (p: any) => {
    const coordinates = await getCoordinates(p.place_id);
    if (!coordinates) return;

    const newState = {
      ...props.stateData,
      placeOfDeparture: {
        label: coordinates.name,
        value: coordinates.name,
        coordinates: {
          lat: coordinates.lat,
          lon: coordinates.lon,
        },
      },
    };

    props.setStateData(newState);
  };

  const handleSelectArrivalPrediction = async (p: any) => {
    const coordinates = await getCoordinates(p.place_id);
    if (!coordinates) return;

    const newState = {
      ...props.stateData,
      placeOfArrival: {
        label: coordinates.name,
        value: coordinates.name,
        coordinates: {
          lat: coordinates.lat,
          lon: coordinates.lon,
        },
      },
    };

    props.setStateData(newState);
  };

  const mapPredictionToOption = (option: any) => ({
    value: option.description,
    label: option.description || option.name,
    place_id: option.place_id,
  });

  const handleSelect = () => {
    callback({ ...props.stateData });
  };

  return (
    <FilterLayout
      config={config}
      title={t(`travelPlaner.PlaceOfDeparture.title`)}
      requirements={requirements}
      siteConfiguration={props.siteConfiguration}
      back={back}
      callback={handleSelect}
    >
      <div className="place-of-departure">
        <div className="   </div>">
          <label className="departure-title" htmlFor="departure-select">
            <strong>{t('travelPlaner.PlaceOfDeparture.departure')}</strong>
          </label>
          <div className="place-of-departure-input">
            <ReactSelect
              inputId="departure-select"
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                NoOptionsMessage: () => (
                  <div style={{ padding: '8px 12px' }}>{t('travelPlaner.Search.noOptions')}</div>
                ),
              }}
              value={
                stateData?.placeOfDeparture
                  ? {
                      value: stateData.placeOfDeparture.value,
                      label: stateData.placeOfDeparture.label,
                      place_id: stateData.placeOfDeparture.place_id,
                    }
                  : null
              }
              options={predictions.map(mapPredictionToOption)}
              onInputChange={handleInput}
              onChange={(selectedOption: any) => {
                if (selectedOption) {
                  handleSelectPrediction(selectedOption);
                }
              }}
              placeholder={t('travelPlaner.Search.input')}
              className="inputBlock"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#b0b0b0',
                  },
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? '#f0f0f0' : 'white',
                  color: '#00533c',
                  '&:hover': {
                    backgroundColor: '#f6faef',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
            <div className="picto">
              <Picto iconKey="magnifyingGlass" />
            </div>
          </div>
        </div>

        {isPlaceOfArrival && (
          <div className="arrival-block">
            <label className="arrival-title" htmlFor="arrival-select">
              <strong>{t('travelPlaner.PlaceOfDeparture.arrival')}</strong>
            </label>
            <div className="place-of-departure-input">
              <ReactSelect
                inputId="arrival-select"
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  NoOptionsMessage: () => (
                    <div style={{ padding: '8px 12px' }}>{t('travelPlaner.Search.noOptions')}</div>
                  ),
                }}
                value={
                  stateData?.placeOfArrival
                    ? {
                        value: stateData.placeOfArrival.value,
                        label: stateData.placeOfArrival.label,
                        place_id: stateData.placeOfArrival.place_id,
                      }
                    : null
                }
                options={arrivalPredictions.map(mapPredictionToOption)}
                onInputChange={handleArrivalInput}
                onChange={(selectedOption: any) => {
                  if (selectedOption) {
                    handleSelectArrivalPrediction(selectedOption);
                  }
                }}
                placeholder={t('travelPlaner.Search.input')}
                className="inputBlock"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#b0b0b0',
                    },
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected ? '#f0f0f0' : 'white',
                    color: '#00533c',
                    '&:hover': {
                      backgroundColor: '#f6faef',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
              <div className="picto">
                <Picto iconKey="magnifyingGlass" />
              </div>
            </div>
          </div>
        )}

        <div className="arrival-checkbox-row">
          <input
            type="checkbox"
            onChange={() => setIsPlaceOfArrival(!isPlaceOfArrival)}
            checked={isPlaceOfArrival}
            name="arrival"
            id="arrival"
          />
          <label htmlFor="arrival" className="arrival-checkbox-label">
            <strong>{t('travelPlaner.PlaceOfDeparture.checkbox')}</strong>
          </label>
        </div>

        <div
          nonce="__nonce__"
          style={{ display: 'none' }}
          ref={(ref) => {
            voidRef.current = ref;
          }}
        />
      </div>
    </FilterLayout>
  );
};
